/**
 * Bonded Matter PDP — match variant picks (metal + stone shape) to product media whose
 * filenames encode render codes (e.g. render-wbg-WNFZ77OV158RG → Oval + Rose Gold).
 */
(function () {
  'use strict';

  const BM_METAL_CODE_ENTRIES = [
    ['950 platinum', 'PT'],
    ['platinum', 'PT'],
    ['rose gold', 'RG'],
    ['white gold', 'WG'],
    ['yellow gold', 'YG'],
    ['18k rose gold', 'RG'],
    ['18k white gold', 'WG'],
    ['18k yellow gold', 'YG'],
    ['18k gold', 'YG'],
  ];

  const BM_SHAPE_CODE_ENTRIES = [
    ['elongated cushion', 'EC'],
    ['elongated radiant', 'ER'],
    ['elongated hexagon', 'EH'],
    ['round brilliant', 'RD'],
    ['asscher', 'AS'],
    ['cushion', 'CU'],
    ['emerald', 'EM'],
    ['marquise', 'MQ'],
    ['princess', 'PR'],
    ['radiant', 'RA'],
    ['trillion', 'TR'],
    ['heart', 'HT'],
    ['oval', 'OV'],
    ['pear', 'PE'],
    ['round', 'RD'],
  ];

  const BM_METAL_CODES = ['RG', 'WG', 'YG', 'PT', 'PL'];
  const BM_SHAPE_CODES = ['OV', 'PE', 'EM', 'RD', 'PR', 'MQ', 'CU', 'AS', 'RA', 'HT', 'TR', 'EC', 'ER', 'EH'];
  const BM_SHAPE_CODES_BY_LENGTH = BM_SHAPE_CODES.slice().sort((a, b) => b.length - a.length);
  const BM_METAL_SUFFIX_PATTERN = BM_METAL_CODES.join('|');

  function lc(v) {
    return (v == null ? '' : String(v)).toLowerCase().trim();
  }

  function metalCodeInHaystack(haystack, code) {
    if (!haystack || !code) return false;
    const h = haystack.toUpperCase();
    const esc = String(code).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (new RegExp(esc + '(?=\\.(webp|jpg|jpeg|png|webm|gif)|[?#]|$)', 'i').test(h)) return true;
    const stem = h.split('?')[0].split('/').pop() || h;
    if (new RegExp(esc + '$', 'i').test(stem.replace(/\.[a-z0-9]+$/i, ''))) return true;
    return false;
  }

  /**
   * Shape codes in render filenames appear in multiple merchant encodings, e.g.:
   *   • Solitaire: `render-wbg-WNFZ77OV158RG`  → OV + carat + metal
   *   • Eternity:  `render-wbg-3N467LEM500RG`  → L + EM + carat + metal
   *   • Radiant:   `render-wbg-RL44UJRA322WG`  → RA + carat + metal (after product id)
   */
  function shapeCodeInHaystack(haystack, code) {
    if (!haystack || !code || BM_SHAPE_CODES.indexOf(code) === -1) return false;
    const h = haystack.toUpperCase();
    const esc = code === 'RD' ? '(?:RD|RO)' : String(code);

    if (new RegExp(esc + '\\d{2,}(?=(?:' + BM_METAL_SUFFIX_PATTERN + ')(?:[^A-Z0-9]|$))').test(h)) {
      return true;
    }
    if (new RegExp('(?:^|[^A-Z])' + esc + '\\d').test(h)) return true;
    if (new RegExp('L' + esc + '\\d').test(h)) return true;
    return false;
  }

  function extractShapeCodeFromHaystack(haystack) {
    if (!haystack) return '';
    for (let i = 0; i < BM_SHAPE_CODES_BY_LENGTH.length; i += 1) {
      const code = BM_SHAPE_CODES_BY_LENGTH[i];
      if (shapeCodeInHaystack(haystack, code)) return code;
    }
    return '';
  }

  function codeInHaystack(haystack, code) {
    if (!haystack || !code) return false;
    if (BM_METAL_CODES.indexOf(code) !== -1) return metalCodeInHaystack(haystack, code);
    if (BM_SHAPE_CODES.indexOf(code) !== -1) return shapeCodeInHaystack(haystack, code);
    return false;
  }

  function isRenderHaystack(haystack) {
    const h = lc(haystack);
    return h.indexOf('render-wbg') !== -1 || h.indexOf('render_wbg') !== -1 || h.indexOf('/render-') !== -1;
  }

  function resolveMetalCode(rawValue, displayName) {
    const candidates = [displayName, rawValue].map(lc).filter(Boolean);
    for (let i = 0; i < BM_METAL_CODE_ENTRIES.length; i += 1) {
      const label = BM_METAL_CODE_ENTRIES[i][0];
      const code = BM_METAL_CODE_ENTRIES[i][1];
      for (let j = 0; j < candidates.length; j += 1) {
        if (candidates[j].indexOf(label) !== -1) return code;
      }
    }
    return '';
  }

  function resolveShapeCode(rawValue) {
    const val = lc(rawValue);
    if (!val) return '';
    for (let i = 0; i < BM_SHAPE_CODE_ENTRIES.length; i += 1) {
      const label = BM_SHAPE_CODE_ENTRIES[i][0];
      const code = BM_SHAPE_CODE_ENTRIES[i][1];
      if (val === label || val.indexOf(label) !== -1) return code;
    }
    return '';
  }

  function parseCodesFromSku(sku) {
    // Read the suffix, not incidental two-letter strings in the product identifier.
    const match = String(sku || '').toUpperCase().match(
      /(OV|PE|EM|RD|RO|PR|MQ|CU|AS|RA|HT|TR|EC|ER|EH)\d{2,}(RG|WG|YG|PT|PL)$/
    );
    return match
      ? { shapeCode: match[1] === 'RO' ? 'RD' : match[1], metalCode: match[2] === 'PL' ? 'PT' : match[2] }
      : { shapeCode: '', metalCode: '' };
  }

  function readSelectedByPosition(variantSelects) {
    const byPos = {};
    variantSelects.querySelectorAll('[data-option-position]').forEach((group) => {
      const pos = parseInt(group.getAttribute('data-option-position'), 10);
      if (!pos || Number.isNaN(pos)) return;
      const radio = group.querySelector('input[type="radio"]:checked');
      if (radio) {
        byPos[pos] = radio.value;
        return;
      }
      const sel = group.querySelector('select');
      if (sel && sel.selectedOptions.length) {
        byPos[pos] = sel.selectedOptions[0].value || sel.value;
      }
    });
    return byPos;
  }

  function parseVariantsJson(scriptEl) {
    if (!scriptEl || !scriptEl.textContent) return [];
    try {
      return JSON.parse(scriptEl.textContent.trim());
    } catch {
      return [];
    }
  }

  function optVal(variant, pos) {
    if (!variant || !pos) return '';
    if (pos === 1) return variant.option1 || '';
    if (pos === 2) return variant.option2 || '';
    if (pos === 3) return variant.option3 || '';
    return '';
  }

  function variantMatchesDomSelection(variant, selectedByPos) {
    if (!variant || !selectedByPos) return true;
    for (let pos = 1; pos <= 3; pos += 1) {
      if (!Object.prototype.hasOwnProperty.call(selectedByPos, pos)) continue;
      if (optVal(variant, pos) !== selectedByPos[pos]) return false;
    }
    return true;
  }

  function readDomPickerCodes(productInfo) {
    let shapeValue = '';
    let metalValue = '';
    let metalDisplay = '';
    if (!productInfo) return { shapeValue, metalValue, metalDisplay };

    const metalInput =
      productInfo.querySelector(
        'variant-selects fieldset.bm-pdp-meta-picker--metal input[type="radio"]:checked'
      ) ||
      productInfo.querySelector('variant-selects fieldset[data-option-name="metal"] input[type="radio"]:checked');

    if (metalInput && !metalInput.disabled) {
      metalValue = metalInput.value || '';
      metalDisplay = metalInput.getAttribute('data-bm-metal-name') || '';
    }

    const shapeInput =
      productInfo.querySelector(
        'variant-selects fieldset.bm-pdp-stone-shape-icons input[type="radio"]:checked'
      ) ||
      productInfo.querySelector('variant-selects fieldset[data-option-name*="shape"] input[type="radio"]:checked');

    if (shapeInput && !shapeInput.disabled) {
      shapeValue = shapeInput.value || '';
    }

    return { shapeValue, metalValue, metalDisplay };
  }

  function resolveVariantFromProductInfo(productInfo) {
    if (!productInfo) return null;
    const variantSelects = productInfo.querySelector('variant-selects');
    if (!variantSelects) return null;

    const selected = readSelectedByPosition(variantSelects);

    const scriptEl = variantSelects.querySelector('script.bm-pdp-variants-json[data-bm-variants]');
    const variants = parseVariantsJson(scriptEl);
    if (variants.length) {
      const match = variants.find((v) => {
        for (let pos = 1; pos <= 3; pos += 1) {
          if (!Object.prototype.hasOwnProperty.call(selected, pos)) continue;
          if (optVal(v, pos) !== selected[pos]) return false;
        }
        return true;
      });
      if (match) return match;
    }

    const selectedVariant = variantSelects.querySelector('[data-selected-variant]');
    if (selectedVariant && selectedVariant.textContent) {
      try {
        const stale = JSON.parse(selectedVariant.textContent.trim());
        if (variantMatchesDomSelection(stale, selected)) return stale;
      } catch {
        return null;
      }
    }
    return null;
  }

  function variantConflictsDomPicker(productInfo, variant) {
    if (!productInfo || !variant) return false;
    const picker = readDomPickerCodes(productInfo);
    const domShape = resolveShapeCode(picker.shapeValue);
    const domMetal = resolveMetalCode(picker.metalValue, picker.metalDisplay);
    const variantShape =
      resolveShapeCode(variant.option1) ||
      resolveShapeCode(variant.option2) ||
      resolveShapeCode(variant.option3);
    const variantMetal =
      resolveMetalCode(variant.option1, '') ||
      resolveMetalCode(variant.option2, '') ||
      resolveMetalCode(variant.option3, '');
    if (domShape && variantShape && domShape !== variantShape) return true;
    if (domMetal && variantMetal && domMetal !== variantMetal) return true;
    return false;
  }

  function buildMatchContext(productInfo, variant) {
    const needles = [];
    const addNeedle = (raw) => {
      const t = lc(raw);
      if (t && needles.indexOf(t) === -1) needles.push(t);
    };

    const picker = readDomPickerCodes(productInfo);
    let shapeValue = picker.shapeValue;
    let metalValue = picker.metalValue;
    let metalDisplay = picker.metalDisplay;

    if (metalValue) {
      addNeedle(metalValue);
      addNeedle(metalDisplay);
    }
    if (shapeValue) {
      addNeedle(shapeValue);
    }

    const domShapeCodeEarly = resolveShapeCode(shapeValue);
    const domMetalCodeEarly = resolveMetalCode(metalValue, metalDisplay);

    if (variant) {
      [variant.option1, variant.option2, variant.option3].forEach((opt) => {
        if (!opt) return;
        const optShape = resolveShapeCode(opt);
        const optMetal = resolveMetalCode(opt, '');
        if (domShapeCodeEarly && optShape && optShape !== domShapeCodeEarly) return;
        if (domMetalCodeEarly && optMetal && optMetal !== domMetalCodeEarly) return;
        addNeedle(opt);
      });
    }

    const expanded = needles.slice();
    for (let i = 0; i < needles.length; i += 1) {
      const tail = needles[i].replace(/^\d+\s*k\s+/i, '').trim();
      if (tail && tail !== needles[i] && expanded.indexOf(tail) === -1) expanded.push(tail);
    }

    const skuRaw = ((variant && variant.sku) || '').toString().trim();
    const fromSku = parseCodesFromSku(skuRaw);
    const staleVariant =
      variant && productInfo && variantConflictsDomPicker(productInfo, variant);

    // Picker UI is source of truth: when Emerald is selected but no sellable variant exists yet,
    // `variant.sku` may still describe Round — do not let SKU filename hits override the DOM.
    const sku = staleVariant ? '' : lc(skuRaw);

    let shapeCode = domShapeCodeEarly;
    let metalCode = domMetalCodeEarly;

    if (!shapeCode && variant) {
      shapeCode =
        fromSku.shapeCode ||
        resolveShapeCode(variant.option1) ||
        resolveShapeCode(variant.option2) ||
        resolveShapeCode(variant.option3);
    }
    if (!metalCode && variant) {
      metalCode =
        fromSku.metalCode ||
        resolveMetalCode(variant.option1, '') ||
        resolveMetalCode(variant.option2, '') ||
        resolveMetalCode(variant.option3, '');
    }

    const variants = parseVariantsJson(productInfo && productInfo.querySelector('script.bm-pdp-variants-json[data-bm-variants]'));

    return {
      mediaVariants: variants.map((v) => ({ sku: lc(v.sku), ...parseCodesFromSku(v.sku) })).filter((v) => v.sku),
      needles: expanded,
      sku,
      shapeCode,
      metalCode,
      shapeValue,
      metalValue,
    };
  }

  function skuInHaystack(haystack, sku) {
    if (!sku) return false;
    const esc = sku.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp('(?:^|[^a-z0-9])' + esc + '(?=[^a-z0-9]|$)', 'i').test(haystack);
  }

  function mediaMatchesContext(haystack, ctx) {
    if (!ctx || (!ctx.shapeCode && !ctx.metalCode)) return true;
    // A shared photograph can encode multiple products. Only this product's catalogue SKUs
    // may supply its shape/metal when present (e.g. Emerald earrings + a Pear ring).
    const variants = (ctx.mediaVariants || []).filter((v) => skuInHaystack(haystack, v.sku));
    if (variants.length) {
      return variants.some((v) =>
        (!ctx.shapeCode || !v.shapeCode || v.shapeCode === ctx.shapeCode) &&
        (!ctx.metalCode || !v.metalCode || v.metalCode === ctx.metalCode)
      );
    }
    const encoded = String(haystack || '').toUpperCase().match(
      /(?:OV|PE|EM|RD|RO|PR|MQ|CU|AS|RA|HT|TR|EC|ER|EH)\d{2,}(?:RG|WG|YG|PT|PL)(?=[^A-Z0-9]|$)/g
    );
    if (encoded) {
      return encoded.some((sku) => {
        const codes = parseCodesFromSku(sku);
        return (!ctx.shapeCode || codes.shapeCode === ctx.shapeCode) &&
          (!ctx.metalCode || codes.metalCode === ctx.metalCode);
      });
    }
    const shape = resolveShapeCode(haystack);
    const metal = resolveMetalCode(haystack, '');
    // Unlabelled editorial/video/model media is shared; explicitly contradictory media is not.
    return (!ctx.shapeCode || !shape || shape === ctx.shapeCode) &&
      (!ctx.metalCode || !metal || metal === ctx.metalCode);
  }

  function mediaHaystack(node) {
    const img = node.querySelector('img');
    return [node.getAttribute('data-src'), node.getAttribute('data-bm-match-haystack'),
      img && (img.getAttribute('src') || img.currentSrc), img && img.getAttribute('alt')].filter(Boolean).join(' ');
  }

  function selectMedia(nodes, ctx, limit) {
    if (!ctx || (!ctx.shapeCode && !ctx.metalCode)) return limit > 0 ? nodes.slice(0, limit) : nodes.slice();
    let selected = nodes.filter((node) => mediaMatchesContext(mediaHaystack(node), ctx));
    const renders = selected.filter((node) => isRenderHaystack(mediaHaystack(node)));
    if (renders.length) {
      const exact = renders.filter((node) => ctx && skuInHaystack(mediaHaystack(node), ctx.sku));
      // Keep all views of the exact render. If that carat has no render, retain one compatible
      // representative rather than every carat's render or an image of another shape/metal.
      const keep = exact.length ? exact : renders.slice(0, 1);
      selected = selected.filter((node) => !renders.includes(node) || keep.includes(node));
    }
    return limit > 0 ? selected.slice(0, limit) : selected;
  }

  const mediaGroups = new WeakMap();
  function filterMediaGroup(root, selector, ctx, limit) {
    if (!root) return { nodes: [], changed: false };
    let all = mediaGroups.get(root);
    if (!all) {
      all = Array.from(root.querySelectorAll(selector));
      mediaGroups.set(root, all);
    }
    const nodes = selectMedia(all, ctx, limit);
    const current = Array.from(root.querySelectorAll(selector));
    const changed = current.length !== nodes.length || current.some((node, i) => node !== nodes[i]);
    if (changed) {
      // Detach, do not recreate: retain model mounts, video state and event handlers on switches.
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia(root);
      all.forEach((node) => node.remove());
      nodes.forEach((node) => root.appendChild(node));
    }
    return { nodes, changed };
  }

  function refreshMediaSlider(slider) {
    if (!slider || slider.destroyed) return;
    slider.update();
    if (slider.pagination) {
      slider.pagination.render();
      slider.pagination.update();
    }
  }

  function scoreHaystack(haystack, ctx) {
    const h = lc(haystack);
    if (!h || !ctx || !mediaMatchesContext(haystack, ctx)) return 0;

    let score = 0;
    if (ctx.sku && h.indexOf(ctx.sku) !== -1) score += 100;

    for (let i = 0; i < ctx.needles.length; i += 1) {
      const needle = ctx.needles[i];
      if (!needle) continue;
      if (/^\d+(\.\d+)?$/.test(needle)) {
        const esc = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        if (new RegExp('(?:^|[^0-9.])' + esc + '(?:[^0-9]|$)').test(h)) score += 1;
      } else if (h.indexOf(needle) !== -1) {
        score += 1;
      }
    }

    if (ctx.shapeCode) {
      const slideShape = extractShapeCodeFromHaystack(haystack);
      if (slideShape && !shapeCodeInHaystack(haystack, ctx.shapeCode)) return 0;
      if (shapeCodeInHaystack(haystack, ctx.shapeCode)) score += 45;
    }

    if (ctx.metalCode && metalCodeInHaystack(haystack, ctx.metalCode)) score += 45;
    if (isRenderHaystack(h)) score += 20;

    return score;
  }

  function pickTargetFromScores(featScore, featHaystack, bmMatch, ctx) {
    if (!bmMatch || bmMatch.score <= 0) return null;

    const bmHaystack = bmMatch.haystack || '';
    const bmIsRender = isRenderHaystack(bmHaystack);
    const featIsRender = isRenderHaystack(featHaystack);

    if (ctx && ctx.shapeCode && featHaystack) {
      const featHasShape = codeInHaystack(featHaystack, ctx.shapeCode);
      const bmHasShape = codeInHaystack(bmHaystack, ctx.shapeCode);
      if (!featHasShape && bmHasShape) return bmMatch;
    }

    if (bmIsRender && !featIsRender) return bmMatch;
    if (bmMatch.score > featScore) return bmMatch;
    if (!featScore && bmIsRender) return bmMatch;
    return null;
  }

  function galleryVariantForProductInfo(productInfo) {
    const variant = resolveVariantFromProductInfo(productInfo);
    if (variant && productInfo && variantConflictsDomPicker(productInfo, variant)) {
      return null;
    }
    return variant;
  }

  function syncProductInfo(productInfo) {
    if (!productInfo) return;
    const sectionId = productInfo.dataset.section;
    const variant = galleryVariantForProductInfo(productInfo);
    const ctx = buildMatchContext(productInfo, variant);
    const hasPickerContext = Boolean(ctx.shapeCode || ctx.metalCode || ctx.needles.length);
    if (!variant && !hasPickerContext) return;

    const gallery = productInfo.querySelector('[id^="MediaGallery-"]');
    if (gallery && typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(variant);
      return;
    }

    if (typeof window.BMSyncPdpHeroGalleryToVariant === 'function' && sectionId) {
      window.BMSyncPdpHeroGalleryToVariant(sectionId, variant);
    }
  }

  function initListeners() {
    const runSync = () => {
      document.querySelectorAll('product-info[id^="MainProduct"]').forEach((el) => {
        try {
          syncProductInfo(el);
        } catch {
          /* keep other PDP scripts alive */
        }
      });
    };

    if (window.FoxTheme?.pubsub?.subscribe && window.FoxTheme?.pubsub?.PUB_SUB_EVENTS) {
      window.FoxTheme.pubsub.subscribe(window.FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, () => {
        window.requestAnimationFrame(runSync);
      });
      window.FoxTheme.pubsub.subscribe(window.FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, () => {
        window.requestAnimationFrame(runSync);
      });
    }

    window.requestAnimationFrame(runSync);
    document.addEventListener('shopify:section:load', () => window.requestAnimationFrame(runSync));

    document.addEventListener('variant:changed', () => {
      window.requestAnimationFrame(runSync);
    });
  }

  window.BmPdpGalleryMatch = {
    lc,
    codeInHaystack,
    shapeCodeInHaystack,
    extractShapeCodeFromHaystack,
    isRenderHaystack,
    resolveMetalCode,
    resolveShapeCode,
    parseCodesFromSku,
    buildMatchContext,
    scoreHaystack,
    pickTargetFromScores,
    resolveVariantFromProductInfo,
    galleryVariantForProductInfo,
    variantConflictsDomPicker,
    syncProductInfo,
    mediaMatchesContext,
    mediaHaystack,
    selectMedia,
    filterMediaGroup,
    refreshMediaSlider,
    skuInHaystack,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initListeners);
  } else {
    initListeners();
  }
})();
